/**
 * Everything the site claims about what Ownly has actually shipped.
 *
 * ONE SOURCE OF TRUTH, ON PURPOSE. Every count on the site is derived from the
 * lists below rather than typed into a heading, because a number typed in two
 * places is a number that goes stale in one of them. If a product launches, add
 * it here and the hero counts, the work page and the schema all move together.
 *
 * THE RULE FOR ADDING ANYTHING HERE: it must be verifiable by a stranger. A live
 * URL, a store listing, a version number. No client results, no revenue, no
 * outcome claims that depend on someone else's business. What we built is ours
 * to state; what it earned somebody is not.
 *
 * Two permanent exclusions:
 *   - The trade-supplies client is under NDA. It is counted in LIVE_PRODUCTS
 *     because the count would otherwise be wrong, and is never named, described
 *     or given a sector, because a description identifies them just as well as
 *     a name would.
 *   - The demo templates are not evidence. They have no client behind them, so
 *     they are not products and they are not sectors.
 */

import { BUILT_COUNT } from "@/lib/built-catalogue";

/* ------------------------------------------------------------------ stores */

export type StorePlatform = "App Store" | "Google Play";

export type ShippedApp = {
  name: string;
  /** Where the app is live right now. Verified against Apple and Google. */
  listings: readonly { platform: StorePlatform; href: string }[];
  /** Set when a platform is submitted but not yet through review. */
  pending?: StorePlatform;
  /** Whose product it is. */
  owner: "client" | "ownly";
};

export const SHIPPED_APPS: readonly ShippedApp[] = [
  {
    name: "GrowthSync",
    owner: "ownly",
    listings: [
      {
        platform: "App Store",
        href: "https://apps.apple.com/app/growthsync/id6756941263",
      },
      {
        platform: "Google Play",
        href: "https://play.google.com/store/apps/details?id=com.growthsync.network",
      },
    ],
  },
  {
    name: "ProFlow Academy",
    owner: "client",
    listings: [
      {
        platform: "App Store",
        href: "https://apps.apple.com/app/proflow-academy-app/id6780488568",
      },
      {
        platform: "Google Play",
        href: "https://play.google.com/store/apps/details?id=com.proflowacademy.app",
      },
    ],
  },
  {
    name: "ProFlow Physiques",
    owner: "client",
    listings: [
      {
        // Cleared App Review and went on sale 2026-09-09, so it is claimed as live now.
        platform: "App Store",
        href: "https://apps.apple.com/app/proflow-physiques/id6794931108",
      },
      {
        platform: "Google Play",
        href: "https://play.google.com/store/apps/details?id=com.proflowphysiques.app",
      },
    ],
  },
] as const;

/** Convenience for the two work-card grids, which take a plain link list. */
export function storeLinksFor(appName: string) {
  const app = SHIPPED_APPS.find((a) => a.name === appName);
  if (!app) return undefined;
  return app.listings.map((l) => ({ label: l.platform, href: l.href }));
}

export const GROWTHSYNC_STORE_LINKS = storeLinksFor("GrowthSync") ?? [];

/* ---------------------------------------------------------------- products */

export type LiveProduct = {
  name: string;
  /** Undefined where the client is under NDA, or the product has no public URL. */
  href?: string;
  kind: "Client site" | "Client app" | "Own product" | "Internal system";
};

/**
 * Everything live and in daily use. This list is the "live products" count.
 * The NDA client is deliberately unnamed and deliberately still counted.
 */
export const LIVE_PRODUCTS: readonly LiveProduct[] = [
  { name: "My Trusted Accountant", href: "https://mytrustedaccountant.co.uk", kind: "Client site" },
  { name: "The DFC Experience", href: "https://thedfcexperience.co.uk/", kind: "Client site" },
  { name: "ProFlow Academy", kind: "Client app" },
  { name: "ProFlow Academy Marketing Site", href: "https://proflowacademy.co.uk/", kind: "Client site" },
  { name: "ProFlow Physiques", kind: "Client app" },
  { name: "ProFlow Physiques Marketing Site", href: "https://proflowphysiques.co.uk/", kind: "Client site" },
  /* Elevate is the founder's own coaching business, not a client. Ownly built
     its website (the app in the stores under the same name is a white-label
     coaching platform app, not our build, so it is not counted). */
  { name: "Elevate Physique Coaching", href: "https://www.elevatephysiquecoaching.com", kind: "Own product" },
  { name: "A client we are not allowed to name", kind: "Client site" },
  { name: "GrowthSync", href: "https://growth-sync.com/", kind: "Own product" },
  { name: "GrowthSync App", kind: "Own product" },
  /* The system we run our own sales on is counted and never named: it is
     private, and naming it is exactly what Tom asked us not to do. */
  { name: "The system we run our own business on", kind: "Internal system" },
  { name: "Ownly Digital", href: "/", kind: "Internal system" },
] as const;

/**
 * Clients we are free to name, each linking to something a stranger can check.
 * Elevate Physique Coaching is deliberately absent: it is the founder's own
 * business, so listing it after "Also built for" would call it a client.
 */
export const NAMED_CLIENTS: readonly { name: string; href: string; what: string }[] = [
  {
    name: "My Trusted Accountant",
    href: "https://mytrustedaccountant.co.uk",
    what: "Accountancy practice",
  },
  {
    name: "The DFC Experience",
    href: "https://thedfcexperience.co.uk/",
    what: "Weddings and events",
  },
  {
    name: "ProFlow Academy",
    href: "https://proflowacademy.co.uk/",
    what: "Coaching app and site",
  },
  {
    name: "ProFlow Physiques",
    href: "https://proflowphysiques.co.uk/",
    what: "Coaching app and site",
  },
] as const;

/* ------------------------------------------------------------------ counts */

const liveAppListings = SHIPPED_APPS.reduce((n, a) => n + a.listings.length, 0);

/**
 * The numbers under the hero. Each one is a claim we can be asked about, so
 * each carries the thing that settles the question.
 */
/*
 * ONE NUMBER, NOT FOUR.
 *
 * Tom, 2026-09-06: "I don't think using numbers when you've only done 3 apps is bragging rights."
 * He is right, and the tiles were worse than he said. "3" was rendered at 4.75rem with a count-up
 * animation, and "12 live products" got there by counting a client's app and that client's
 * marketing site as two, which anyone paying attention reads as padding. A small number displayed
 * enormously draws attention to being small.
 *
 * What survives is the one figure nobody else at this size can claim: 20 languages, with a build
 * that fails on a single untranslated string. The rest of the proof is now names a stranger can
 * open, which is what this business actually has and what no count can fake. See ProofCounts.tsx.
 */
export const PROOF_COUNTS = [
  {
    value: "Multilingual",
    label: "In every language it is offered in",
    detail: "And the build fails if a single string is left in English.",
    href: "/built#sites-and-content",
  },
] as const;

/* ----------------------------------------------------------- what we build */

/** Sectors we have actually delivered in. The NDA client's sector is not here. */
export const SECTORS: readonly string[] = [
  "Accountancy",
  "Weddings and events",
  "Physique coaching",
  "Business software",
] as const;

/**
 * Delivery standards and scope-dependent capabilities. Scope labels prevent a
 * brochure website from implicitly promising payments, contracts or user accounts.
 */
export const SHIP_STANDARDS: readonly { title: string; detail: string; scope?: string }[] = [
  {
    title: "Payments connected to the work",
    scope: "When payments are in scope",
    detail:
      "Stripe checkout, deposits, instalments and payment links, wired to the job they belong to rather than a separate dashboard.",
  },
  {
    title: "Contracts sent with the quote",
    scope: "When e-signatures are in scope",
    detail:
      "E-signature attached to the deal, so the agreement goes out with the quote instead of chasing it a week later.",
  },
  {
    title: "Control over your customer data",
    scope: "For products with customer accounts",
    detail:
      "Export and account deletion flows planned alongside the data the product holds and the requirements that apply to it.",
  },
  {
    title: "Access limited to the right people",
    scope: "For products with protected data",
    detail:
      "Permissions enforced where the data is accessed, so each user can only reach the information and actions intended for them.",
  },
  {
    title: "We hear about breakages first",
    detail:
      "Error monitoring on the browser and the server, plus alerts when a scheduled job quietly stops running.",
  },
  {
    title: "Forms that bots cannot farm",
    detail:
      "Rate limiting and bot protection on every public form, so your inbox stays enquiries rather than noise.",
  },
  {
    title: "Legal pages that match reality",
    detail:
      "Privacy policy, terms and cookie consent that describe what the software actually collects, not a template.",
  },
  {
    title: "Readable by everyone",
    detail:
      "Accessible contrast, keyboard navigation and real alt text, checked rather than assumed.",
  },
] as const;

/**
 * Dated proof that delivery did not stop at launch. Newest first.
 * Add a line when something meaningful ships. Keep it to things a visitor could
 * plausibly see or use, not refactors.
 */
export const RECENTLY_SHIPPED: readonly {
  date: string;
  iso: string;
  product: string;
  what: string;
}[] = [
  {
    date: "8 September 2026",
    iso: "2026-09-08",
    product: "ProFlow Academy",
    what: "Version 1.0.8 on the App Store and Google Play, with the check-ins waiting for a coach now on their home screen, and check-in days set per athlete so reminders only reach the people they apply to.",
  },
  {
    date: "7 September 2026",
    iso: "2026-09-07",
    product: "GrowthSync",
    what: "Version 2.8 through Apple review, with signing up cut back to the essentials so a new member is meeting people in about two minutes.",
  },
  {
    date: "2 September 2026",
    iso: "2026-09-02",
    product: "ProFlow Physiques",
    what: "A food log worth reading, with history and averages, so a coach can see what a client tracking macros actually ate.",
  },
  {
    date: "30 August 2026",
    iso: "2026-08-30",
    product: "GrowthSync",
    what: "Version 1.0.7 on Google Play, where a profile carries up to three industries and Discover matches on them, so picking the three that fit beats listing everything.",
  },
  {
    date: "26 August 2026",
    iso: "2026-08-26",
    product: "My Trusted Accountant",
    what: "The practice now tells search engines the places it actually serves, Leyland, Preston, Chorley and Lancashire, instead of only United Kingdom.",
  },
  {
    date: "25 August 2026",
    iso: "2026-08-25",
    product: "ProFlow Physiques",
    what: "Version 1.1.3 on Google Play, where finishing a workout finishes that workout rather than the whole programme, and every exercise shows what you lifted last time.",
  },
  {
    date: "25 August 2026",
    iso: "2026-08-25",
    product: "ProFlow Academy",
    what: "Version 1.0.7 on Google Play, adding voice notes to messages, so a coach records and listens back instead of typing it out.",
  },
  {
    date: "24 August 2026",
    iso: "2026-08-24",
    product: "GrowthSync",
    what: "The website rebuilt as a proper multi-page site, with a page for each feature and each kind of member instead of one long scroll.",
  },
  {
    date: "21 August 2026",
    iso: "2026-08-21",
    product: "The DFC Experience",
    what: "The office changes the password on the artist agreement area themselves now, instead of asking us and waiting for a release every time somebody leaves.",
  },
] as const;
